/**
 * AdminShiprocket — tenant-admin Shiprocket configuration & health.
 *
 * iter124 — this page is ONLY reachable when the Super Admin has
 * flipped ``tenant.shiprocket_enabled`` to true (the nav item is
 * conditionally rendered in ``AdminLayout``; if a user bookmarks the
 * URL directly with the toggle off, the backend returns
 * ``{enabled: false}`` and this component renders a hard-disabled
 * empty state).
 *
 * Sections:
 *   1. Status card — last test result, cached-token TTL.
 *   2. Credentials form — Shiprocket API email + password. Password is
 *      write-only (server returns ``has_password: bool`` instead of
 *      the value).
 *   3. Test Connection button — persists the outcome server-side.
 *   4. Pickup addresses — "Our Store Address → Shiprocket Pickup
 *      Address" link. Previously this was a single free-text field the
 *      admin had to fill in by hand-typing a nickname that had to
 *      EXACTLY match an address already created directly inside
 *      Shiprocket's own dashboard, with no way to see what existed or
 *      to register a new one from here at all — a genuine missing
 *      piece, not just a UX rough edge. Now lists real registered
 *      addresses (``GET /pickup-locations``) to pick from, and can
 *      register our store as a brand-new one
 *      (``POST /pickup-locations``) using Shiprocket's own
 *      "add pickup address" API.
 */
import { useEffect, useState } from "react";
import { Truck, CheckCircle2, XCircle, Loader2, ExternalLink, MapPin, Plus, Check, Webhook, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Field } from "./AdminRestaurants";
import { useTenant } from "../../contexts/TenantContext";

const SHIPROCKET_WEBHOOK_BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/webhook/shiprocket`;

const EMPTY_PICKUP_FORM = {
    pickup_location: "", name: "", email: "", phone: "",
    address: "", address_2: "", city: "", state: "", pin_code: "", country: "India",
};

export default function AdminShiprocket() {
    const { tenant } = useTenant();
    // Tenant-scoped webhook URL (2026-08-20) — the id-suffixed path lets
    // the backend guarantee a webhook can never update another tenant's
    // order, even if two independent Shiprocket accounts happen to
    // produce the same order_id/AWB (see shiprocket_webhook.py). Falls
    // back to the bare base URL for the brief window before the tenant
    // context has loaded, so the field never renders truly empty.
    const webhookUrl = tenant?.id ? `${SHIPROCKET_WEBHOOK_BASE}/${tenant.id}` : SHIPROCKET_WEBHOOK_BASE;
    const [state, setState] = useState({ loading: true, enabled: false, config: null });
    const [form, setForm] = useState({ api_email: "", api_password: "" });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const [pickupLocations, setPickupLocations] = useState([]);
    const [pickupLoading, setPickupLoading] = useState(false);
    const [pickupError, setPickupError] = useState("");
    const [selecting, setSelecting] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState(EMPTY_PICKUP_FORM);
    const [addSaving, setAddSaving] = useState(false);

    const [webhookSecret, setWebhookSecret] = useState("");     // shown once, right after generating
    const [generatingSecret, setGeneratingSecret] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/shiprocket/config");
            setState({ loading: false, enabled: !!data.enabled, config: data.config });
            if (data.config) {
                setForm((f) => ({ ...f, api_email: data.config.api_email || "" }));
                if (data.config.api_email) loadPickupLocations();
            }
        } catch (e) {
            setState({ loading: false, enabled: false, config: null });
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const loadPickupLocations = async () => {
        setPickupLoading(true);
        setPickupError("");
        try {
            const { data } = await api.get("/admin/shiprocket/pickup-locations");
            setPickupLocations(data.pickup_locations || []);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setPickupError(d?.message || (typeof d === "string" ? d : "Could not fetch pickup addresses"));
        } finally {
            setPickupLoading(false);
        }
    };

    const selectPickupLocation = async (name) => {
        setSelecting(name);
        try {
            const { data } = await api.patch("/admin/shiprocket/config", { default_pickup_location: name });
            setState((s) => ({ ...s, config: data.config }));
            toast.success(`Using "${name}" as the pickup address`);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Failed to set pickup address");
        } finally {
            setSelecting("");
        }
    };

    const addPickupLocation = async () => {
        setAddSaving(true);
        try {
            const { data } = await api.post("/admin/shiprocket/pickup-locations", addForm);
            setState((s) => ({ ...s, config: data.config }));
            toast.success(`"${data.pickup_location}" registered and set as the pickup address`);
            setShowAddForm(false);
            setAddForm(EMPTY_PICKUP_FORM);
            loadPickupLocations();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.message || (typeof d === "string" ? d : "Failed to register pickup address"));
        } finally {
            setAddSaving(false);
        }
    };

    const generateWebhookSecret = async () => {
        setGeneratingSecret(true);
        try {
            const { data } = await api.post("/admin/shiprocket/webhook/generate-secret");
            setWebhookSecret(data.webhook_secret);
            setState((s) => ({ ...s, config: { ...s.config, has_webhook_secret: true } }));
            toast.success("Webhook secret generated — copy it into Shiprocket now, it won't be shown again");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Failed to generate webhook secret");
        } finally {
            setGeneratingSecret(false);
        }
    };

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch {
            toast.error("Could not copy — select and copy manually");
        }
    };

    const save = async () => {
        // Only send fields the user actually edited so blank-password
        // doesn't wipe a stored password. api_password is only sent
        // when the user typed something new into the input.
        // default_pickup_location is no longer edited here — it's set
        // by picking/creating a pickup address below.
        const patch = { api_email: form.api_email };
        if (form.api_password) patch.api_password = form.api_password;
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/shiprocket/config", patch);
            setState((s) => ({ ...s, config: data.config }));
            setForm((f) => ({ ...f, api_password: "" }));       // clear password field after save
            toast.success("Shiprocket configuration saved");
            if (data.config?.api_email) loadPickupLocations();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Failed to save configuration");
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        try {
            const { data } = await api.post("/admin/shiprocket/test-connection");
            setState((s) => ({ ...s, config: data.config }));
            toast.success("Shiprocket connection is active");
            loadPickupLocations();
        } catch (e) {
            const d = e?.response?.data?.detail;
            const code = d?.code || "UNKNOWN";
            const msg = d?.message || "Test connection failed";
            toast.error(`${code}: ${msg}`);
            // Still refresh to pick up last_test_error from server.
            load();
        } finally {
            setTesting(false);
        }
    };

    if (state.loading) {
        return (
            <div className="p-6 flex items-center gap-2 text-muted-foreground">
                <Loader2 size={18} className="animate-spin" /> Loading Shiprocket configuration…
            </div>
        );
    }

    if (!state.enabled) {
        return (
            <div className="p-6" data-testid="shiprocket-disabled">
                <div className="max-w-xl bg-muted/40 border-2 border-dashed rounded-2xl p-8 text-center">
                    <Truck size={40} className="mx-auto text-muted-foreground mb-3" />
                    <h2 className="text-xl font-bold mb-1">Shiprocket is not enabled</h2>
                    <p className="text-sm text-muted-foreground">
                        Ask your platform Super Admin to enable Shiprocket for your tenant. Once enabled,
                        you&apos;ll be able to configure API credentials and create shipments here.
                    </p>
                </div>
            </div>
        );
    }

    const cfg = state.config || {};
    const testOk = cfg.last_test_ok === true;
    const testFail = cfg.last_test_ok === false;

    return (
        <div className="p-4 md:p-6 space-y-4 max-w-3xl" data-testid="shiprocket-page">
            <div className="flex items-center gap-2">
                <Truck size={20} />
                <h1 className="text-2xl font-bold">Shiprocket</h1>
            </div>

            {/* Status card */}
            <div className="rounded-2xl border bg-card p-4" data-testid="shiprocket-status-card">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">Connection status</p>
                {!cfg.api_email ? (
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <XCircle size={16} /> <span className="text-sm font-semibold">Credentials not configured yet</span>
                    </div>
                ) : testOk ? (
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400" data-testid="shiprocket-status-ok">
                        <CheckCircle2 size={16} />
                        <span className="text-sm font-semibold">Connected as {cfg.api_email}</span>
                        {cfg.token_expires_at && (
                            <span className="text-xs text-muted-foreground">
                                (token valid until {new Date(cfg.token_expires_at).toLocaleDateString()})
                            </span>
                        )}
                    </div>
                ) : testFail ? (
                    <div data-testid="shiprocket-status-fail">
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                            <XCircle size={16} />
                            <span className="text-sm font-semibold">Last test failed</span>
                        </div>
                        {cfg.last_test_error && (
                            <p className="text-xs text-muted-foreground mt-1 pl-6">{cfg.last_test_error}</p>
                        )}
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">Credentials saved — no test run yet.</div>
                )}
                {cfg.last_test_at && (
                    <p className="text-[11px] text-muted-foreground mt-2">
                        Last tested: {new Date(cfg.last_test_at).toLocaleString()}
                    </p>
                )}
                {/* Bug found in production (mani tenant): this used to warn
                    about a blank free-text field the admin had to guess a
                    matching name for. Now points at the real fix — pick or
                    register a pickup address below — since auth being OK
                    doesn't mean a pickup address is set (unrelated checks). */}
                {cfg.api_email && !cfg.default_pickup_location && (
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 mt-2" data-testid="shiprocket-pickup-missing-warning">
                        <XCircle size={14} />
                        <span className="text-xs font-semibold">
                            No pickup address selected — sending an order to Shiprocket will fail until you pick or add one below.
                        </span>
                    </div>
                )}
            </div>

            {/* Config form */}
            <div className="rounded-2xl border bg-card p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-3">API credentials</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Shiprocket API email">
                        <input
                            type="email"
                            className="inp"
                            data-testid="shiprocket-email-input"
                            value={form.api_email}
                            onChange={(e) => setForm({ ...form, api_email: e.target.value })}
                            placeholder="api-user@your-brand.com"
                        />
                    </Field>
                    <Field label={`API password ${cfg.has_password ? "(leave blank to keep current)" : ""}`}>
                        <input
                            type="password"
                            className="inp"
                            data-testid="shiprocket-password-input"
                            value={form.api_password}
                            onChange={(e) => setForm({ ...form, api_password: e.target.value })}
                            placeholder={cfg.has_password ? "•••••••• (unchanged)" : "Enter password"}
                        />
                    </Field>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="shiprocket-save-btn"
                        className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        Save credentials
                    </button>
                    <button
                        onClick={testConnection}
                        disabled={testing || !cfg.api_email}
                        data-testid="shiprocket-test-btn"
                        className="border rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {testing && <Loader2 size={14} className="animate-spin" />}
                        Test connection
                    </button>
                    <a
                        href="https://app.shiprocket.in/api-user"
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-muted-foreground hover:underline ml-auto inline-flex items-center gap-1"
                    >
                        Create an API user <ExternalLink size={11} />
                    </a>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                    Tip: create a dedicated API user in your Shiprocket dashboard (Settings → API → Configure)
                    rather than using your primary login.
                </p>
            </div>

            {/* Pickup addresses — "Our Store Address → Shiprocket Pickup
                Address" link. Previously missing entirely: no way to see
                what addresses existed in Shiprocket, or to register our
                store as a new one, from inside this app at all. */}
            {cfg.api_email && (
                <div className="rounded-2xl border bg-card p-4" data-testid="shiprocket-pickup-section">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Pickup addresses</p>
                        <button
                            onClick={() => setShowAddForm((v) => !v)}
                            data-testid="shiprocket-pickup-add-toggle"
                            className="text-xs font-bold text-primary inline-flex items-center gap-1"
                        >
                            <Plus size={13} /> {showAddForm ? "Cancel" : "Add pickup address"}
                        </button>
                    </div>

                    {pickupLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                            <Loader2 size={14} className="animate-spin" /> Loading pickup addresses…
                        </div>
                    ) : pickupError ? (
                        <div className="text-sm text-red-700 dark:text-red-400" data-testid="shiprocket-pickup-list-error">{pickupError}</div>
                    ) : pickupLocations.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No pickup addresses registered on this Shiprocket account yet. Add your store below —
                            couriers pick up from whichever address you select here.
                        </p>
                    ) : (
                        <div className="space-y-2" data-testid="shiprocket-pickup-list">
                            {pickupLocations.map((p) => {
                                const active = p.pickup_location === cfg.default_pickup_location;
                                return (
                                    <button
                                        key={p.pickup_location}
                                        onClick={() => selectPickupLocation(p.pickup_location)}
                                        disabled={!!selecting}
                                        data-testid={`shiprocket-pickup-option-${p.pickup_location}`}
                                        className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition disabled:opacity-60 ${active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                                    >
                                        <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>
                                            {active && <Check size={12} />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-sm flex items-center gap-1.5">
                                                <MapPin size={13} className="text-muted-foreground" /> {p.pickup_location}
                                                {active && <span className="text-[10px] uppercase tracking-wider font-bold text-primary">In use</span>}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {[p.address, p.city, p.state, p.pin_code].filter(Boolean).join(", ")}
                                            </p>
                                        </div>
                                        {selecting === p.pickup_location && <Loader2 size={14} className="animate-spin shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {showAddForm && (
                        <div className="mt-4 pt-4 border-t border-border" data-testid="shiprocket-pickup-add-form">
                            <p className="text-xs font-bold mb-3">Register our store as a new pickup address</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Pickup location name">
                                    <input className="inp" data-testid="pickup-form-name-tag" value={addForm.pickup_location}
                                        onChange={(e) => setAddForm({ ...addForm, pickup_location: e.target.value })}
                                        placeholder="e.g. Main Warehouse" />
                                </Field>
                                <Field label="Contact person name">
                                    <input className="inp" data-testid="pickup-form-contact-name" value={addForm.name}
                                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                        placeholder="Who couriers should ask for" />
                                </Field>
                                <Field label="Contact phone">
                                    <input className="inp" data-testid="pickup-form-phone" value={addForm.phone}
                                        onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                                        placeholder="10-digit mobile number" />
                                </Field>
                                <Field label="Contact email">
                                    <input type="email" className="inp" data-testid="pickup-form-email" value={addForm.email}
                                        onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                                        placeholder="store@yourbrand.com" />
                                </Field>
                                <Field full label="Address line 1">
                                    <input className="inp" data-testid="pickup-form-address" value={addForm.address}
                                        onChange={(e) => setAddForm({ ...addForm, address: e.target.value })}
                                        placeholder="House / building / street" />
                                </Field>
                                <Field full label="Address line 2 (optional)">
                                    <input className="inp" data-testid="pickup-form-address2" value={addForm.address_2}
                                        onChange={(e) => setAddForm({ ...addForm, address_2: e.target.value })} />
                                </Field>
                                <Field label="City">
                                    <input className="inp" data-testid="pickup-form-city" value={addForm.city}
                                        onChange={(e) => setAddForm({ ...addForm, city: e.target.value })} />
                                </Field>
                                <Field label="State">
                                    <input className="inp" data-testid="pickup-form-state" value={addForm.state}
                                        onChange={(e) => setAddForm({ ...addForm, state: e.target.value })} />
                                </Field>
                                <Field label="Pincode">
                                    <input className="inp" data-testid="pickup-form-pincode" value={addForm.pin_code}
                                        onChange={(e) => setAddForm({ ...addForm, pin_code: e.target.value })}
                                        placeholder="6-digit pincode" />
                                </Field>
                                <Field label="Country">
                                    <input className="inp" data-testid="pickup-form-country" value={addForm.country}
                                        onChange={(e) => setAddForm({ ...addForm, country: e.target.value })} />
                                </Field>
                            </div>
                            <button
                                onClick={addPickupLocation}
                                disabled={addSaving}
                                data-testid="shiprocket-pickup-add-save"
                                className="mt-4 bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {addSaving && <Loader2 size={14} className="animate-spin" />}
                                Register with Shiprocket
                            </button>
                            <p className="text-[11px] text-muted-foreground mt-2">
                                This calls Shiprocket&apos;s own "add pickup address" API directly — it becomes a real,
                                permanent address on your Shiprocket account, immediately usable for real shipments.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Webhook — real-time status sync. Without this, statuses only
                update when someone clicks "Refresh tracking" on an order;
                configuring this in Shiprocket's own dashboard makes our
                database update automatically as the shipment progresses.
                "Refresh tracking" keeps working as a manual fallback
                either way — this is additive, not a replacement. */}
            {cfg.api_email && (
                <div className="rounded-2xl border bg-card p-4" data-testid="shiprocket-webhook-section">
                    <div className="flex items-center gap-2 mb-3">
                        <Webhook size={14} className="text-muted-foreground" />
                        <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
                            Webhook (automatic status sync)
                        </p>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                        Optional. Paste this URL and a generated secret into your Shiprocket dashboard under
                        <span className="font-semibold"> Settings → API → Webhook</span>, then use their
                        "Test Webhook" button to confirm it reaches us. Once configured, order statuses update
                        here automatically as your shipments move — "Refresh tracking" still works any time too.
                    </p>
                    <Field label="Webhook URL">
                        <div className="flex items-center gap-2">
                            <input className="inp font-mono text-xs" data-testid="shiprocket-webhook-url" readOnly value={webhookUrl} />
                            <button
                                type="button"
                                onClick={() => copyToClipboard(webhookUrl, "Webhook URL")}
                                data-testid="shiprocket-webhook-url-copy"
                                className="shrink-0 p-2.5 rounded-xl border hover:bg-muted"
                                title="Copy"
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                    </Field>
                    <div className="mt-3">
                        {webhookSecret ? (
                            <Field label="Webhook secret — copy it now, it won't be shown again">
                                <div className="flex items-center gap-2">
                                    <input className="inp font-mono text-xs" data-testid="shiprocket-webhook-secret-value" readOnly value={webhookSecret} />
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard(webhookSecret, "Webhook secret")}
                                        className="shrink-0 p-2.5 rounded-xl border hover:bg-muted"
                                        title="Copy"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                            </Field>
                        ) : (
                            <div className="flex items-center gap-3">
                                {cfg.has_webhook_secret && (
                                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                        <CheckCircle2 size={13} className="text-emerald-600" /> Secret already generated
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={generateWebhookSecret}
                                    disabled={generatingSecret}
                                    data-testid="shiprocket-webhook-generate-btn"
                                    className="border rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                >
                                    {generatingSecret && <Loader2 size={14} className="animate-spin" />}
                                    {cfg.has_webhook_secret ? "Regenerate secret" : "Generate secret"}
                                </button>
                            </div>
                        )}
                    </div>
                    {cfg.has_webhook_secret && !webhookSecret && (
                        <p className="text-[11px] text-muted-foreground mt-2">
                            Regenerating replaces the old secret — you&apos;ll need to update it in Shiprocket&apos;s
                            dashboard too, or their webhook calls will be rejected until you do.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
